'use client';

import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { AnnouncementBannerGroup as AnnouncementBannerGroupUi } from '@/ui-components/organisms/AnnouncementBannerGroup/AnnouncementBannerGroup';
import { ComponentProps, useState } from 'react';
import { ImageWrapper } from '@/shared-components/ImageWrapper/ImageWrapper';
import { siteLinkToWeb2ButtonProps } from '@/components-2.0/contentful/Link/linkPropUtils';
import { RenderRTE } from '@/components-2.0/structure/RenderRTE';
import { type ButtonProps } from '@/ui-components/atoms/Button/Button';

dayjs.extend(isBetween);

type AnnouncementBannerGroupUiProps = ComponentProps<typeof AnnouncementBannerGroupUi>;

const BACKGROUND_COLOR_TO_COLOR_SET: {
    [backgroundColor: string]: AnnouncementBannerGroupUiProps['borderColorSet'];
} = Object.freeze({
    black: 'inverse',
    white: 'base',
    'brand blue': 'sxmBlue',
});

const SESSION_STORAGE_KEY = 'closedAnnouncementIds';
const GEO_COOKIE_NAME = 'sxm_geo';
const CANADA_SITE_BASE_URL = 'https://www.siriusxm.ca/?intcmp=CAdotcom';

interface AnnouncementBannersProps {
    announcementAssets: SiteAnnouncement[];
    hideGlobalAnnouncement?: boolean;
    canadianEquivalentPageVanity?: string;
}

export function AnnouncementBanners({
    announcementAssets = [],
    hideGlobalAnnouncement,
    canadianEquivalentPageVanity,
}: AnnouncementBannersProps): React.JSX.Element {
    const [isCanadianUser] = useState(() => isCanadaGeo());
    const [closedAnnouncementIds, setClosedAnnouncementIds] = useState<string[]>(() =>
        getClosedAnnouncementIdsFromSessionStorage(),
    );

    const dismissAnnouncement = (closedBannerId: string) => {
        setClosedAnnouncementIds((previousClosedBannerIds) => {
            const updatedClosedBannerIds = Array.from(new Set(previousClosedBannerIds).add(closedBannerId));
            setClosedAnnouncementIdsInSessionStorage(updatedClosedBannerIds);
            return updatedClosedBannerIds;
        });
    };

    const dedupedAnnouncements = announcementAssets.filter(
        (asset, index, allAssets) => index === allAssets.findIndex((otherAsset) => otherAsset.cms_id === asset.cms_id),
    );

    const visibleAnnouncements = dedupedAnnouncements
        .filter(
            (asset) =>
                !closedAnnouncementIds.includes(asset.cms_id) &&
                dayjs().isBetween(asset.fields.begin_date, asset.fields.finish_date, 'minute', '[)') &&
                (!hideGlobalAnnouncement || !isGlobalAnnouncement(asset)) &&
                (!isCanadaUsersAnnouncement(asset) || isCanadianUser),
        );
    const filteredAnnouncements = visibleAnnouncements
        .sort((assetA, assetB) => {
            const assetAPriority = getAnnouncementPriority(assetA, isCanadianUser);
            const assetBPriority = getAnnouncementPriority(assetB, isCanadianUser);

            if (assetAPriority !== assetBPriority) {
                return assetAPriority - assetBPriority;
            }

            return 0;
        });

    const banners = filteredAnnouncements.map((asset) => {
        if (isCanadaUsersAnnouncement(asset)) {
            return parseCanadaBannerData(asset, dismissAnnouncement, canadianEquivalentPageVanity);
        }

        return parseBannerData(asset, dismissAnnouncement);
    });

    return <AnnouncementBannerGroupUi banners={banners} borderColorSet={banners[0]?.colorSet} />;
}

const parseBannerData = (
    { cms_id, fields }: SiteAnnouncement,
    dismissButtonCallback: (closedBannerId: string) => void,
): AnnouncementBannerGroupUiProps['banners'][0] => ({
    leadingIcon: fields.image?.url && (
        <ImageWrapper
            src={fields.image.url}
            alt={fields.image.altText ?? 'Announcement'}
            optimizedWidth={28}
            optimizedHeight={28}
        />
    ),
    text: <RenderRTE contentfulDoc={fields.description} />,
    onDismissButtonClick: fields.closeable_announcement ? () => dismissButtonCallback(cms_id) : undefined,
    colorSet: getAnnouncementColorSet(fields.background_color),
    ctas: fields.links?.map((link) => siteLinkToWeb2ButtonProps(link.fields)),
});

const parseCanadaBannerData = (
    { cms_id, fields }: SiteAnnouncement,
    dismissButtonCallback: (closedBannerId: string) => void,
    canadianEquivalentPageVanity?: string,
): AnnouncementBannerGroupUiProps['banners'][0] => ({
    ariaLabel: 'Canada location announcement',
    leadingIcon: fields.image?.url && (
        <ImageWrapper
            src={fields.image.url}
            alt={fields.image.altText ?? 'Announcement'}
            optimizedWidth={16}
            optimizedHeight={16}
        />
    ),
    role: 'region',
    text: <RenderRTE contentfulDoc={fields.description} />,
    // Canada global banners should always be dismissible, either via close icon or "stay" CTA.
    onDismissButtonClick: () => dismissButtonCallback(cms_id),
    colorSet: getAnnouncementColorSet(fields.background_color),
    ctas: buildCanadaAnnouncementCtas(fields.links, dismissButtonCallback, cms_id, canadianEquivalentPageVanity),
    layout: 'geo',
});

const buildCanadaAnnouncementCtas = (
    links: SiteAnnouncement['fields']['links'],
    dismissButtonCallback: (closedBannerId: string) => void,
    cmsId: string,
    canadianEquivalentPageVanity?: string,
): ButtonProps[] => {
    const [stayOnSiteLink, goToCanadaLink] = links ?? [];
    const stayOnSiteLabel = getCanadaCtaLabel(stayOnSiteLink);
    const goToCanadaLabel = getCanadaCtaLabel(goToCanadaLink);

    const stayOnSiteCta: ButtonProps = {
        children: stayOnSiteLabel,
        href: undefined,
        linkTarget: undefined,
        onClick: () => dismissButtonCallback(cmsId),
        type: 'button',
        variant: 'tertiary',
        ...(stayOnSiteLabel ? { 'data-linkname': stayOnSiteLabel } : {}),
    };

    const goToCanadaCta: ButtonProps = {
        children: goToCanadaLabel,
        href: getCanadaEquivalentUrl(canadianEquivalentPageVanity),
        linkTarget: '_self',
        onClick: undefined,
        type: 'button',
        variant: 'tertiary',
        ...(goToCanadaLabel ? { 'data-linkname': goToCanadaLabel } : {}),
    };

    return [stayOnSiteCta, goToCanadaCta].filter((cta) => !!cta.children);
};

const isGlobalAnnouncement = (asset: SiteAnnouncement): boolean =>
    asset.fields.global_banner === 'Yes' || asset.fields.global_banner === 'Canada Users';

const isCanadaUsersAnnouncement = (asset: SiteAnnouncement): boolean => asset.fields.global_banner === 'Canada Users';

const getAnnouncementPriority = (asset: SiteAnnouncement, isCanadianUser: boolean): number => {
    if (isCanadianUser && isCanadaUsersAnnouncement(asset)) {
        return 0;
    }

    if (isGlobalAnnouncement(asset)) {
        return 1;
    }

    return 2;
};

const getCanadaEquivalentUrl = (canadianEquivalentPageVanity?: string): string => {
    const sanitizedVanity = canadianEquivalentPageVanity?.trim();
    if (!sanitizedVanity || sanitizedVanity === '/') {
        return CANADA_SITE_BASE_URL;
    }

    return sanitizedVanity;
};

const getCanadaCtaLabel = (link?: SiteLink): string | undefined => link?.fields.link_text ?? link?.fields.name;

const getAnnouncementColorSet = (
    backgroundColor?: string,
): AnnouncementBannerGroupUiProps['borderColorSet'] => {
    const normalizedBackgroundColor = backgroundColor?.trim().toLowerCase();
    return (normalizedBackgroundColor && BACKGROUND_COLOR_TO_COLOR_SET[normalizedBackgroundColor]) || 'base';
};

const getCookiesByName = (name: string): string[] => {
    if (typeof document === 'undefined') {
        return [];
    }

    return document.cookie
        .split('; ')
        .map((cookie) => cookie.trim())
        .filter((cookie) => cookie.startsWith(`${name}=`))
        .map((cookie) => decodeURIComponent(cookie.split('=').slice(1).join('=')));
};

const isCanadaGeo = (): boolean => {
    const geos = getCookiesByName(GEO_COOKIE_NAME);
    return geos.some((geo) => geo.endsWith(':CA'));
};

const getClosedAnnouncementIdsFromSessionStorage = (): string[] => {
    if (typeof window === 'undefined') {
        return [];
    }

    try {
        return window.sessionStorage.getItem(SESSION_STORAGE_KEY)?.split(',') ?? [];
    } catch (e) {
        console.error(e);
        return [];
    }
};

const setClosedAnnouncementIdsInSessionStorage = (closedAnnouncementIds: string[]): void => {
    if (typeof window === 'undefined') {
        return;
    }

    try {
        window.sessionStorage.setItem(SESSION_STORAGE_KEY, closedAnnouncementIds.join(','));
    } catch (e) {
        console.error(e);
    }
};
